<template>
	<el-dialog
		:visible.sync="dialogVisible"
		:title="$t('referenceData.importTitle')"
		width="1200px"
		destroy-on-close
		@close="handleClose"
	>
		<el-steps :active="currentStep" finish-status="success" align-center class="mb-20">
			<el-step :title="$t('referenceData.importStepUpload')" />
			<el-step :title="$t('referenceData.importStepChooseKey')" />
			<el-step :title="$t('referenceData.importStepValidate')" />
			<el-step :title="$t('referenceData.importStepConfirm')" />
		</el-steps>

		<!-- Step 0: Upload -->
		<div v-show="currentStep === STEPS.UPLOAD" class="import-step">
			<el-upload
				ref="upload"
				class="import-upload"
				action=""
				:auto-upload="false"
				:on-change="handleFileChange"
				:show-file-list="false"
				:disabled="loading"
				drag
				accept=".csv"
			>
				<i class="el-icon-upload"></i>
				<div class="el-upload__text">
					{{ $t('referenceData.dragOrClick') }}
				</div>
			</el-upload>
			<el-alert
				v-if="error"
				:title="error"
				type="error"
				show-icon class="mt-10"
				:closable="false" />
		</div>

		<!-- Step-specific controls (steps 1-3) -->
		<div v-if="currentStep >= STEPS.CHOOSE_KEY" class="import-step">
			<!-- Step 1: Choose key -->
			<el-form v-show="currentStep === STEPS.CHOOSE_KEY" inline class="mb-15">
				<el-form-item :label="$t('referenceData.keyColumnLabel')">
					<el-select v-model="selectedKeyColumn" :placeholder="$t('referenceData.selectKeyColumn')" style="width: 280px">
						<el-option v-for="col in previewColumns" :key="col" :label="col" :value="col" />
					</el-select>
				</el-form-item>
			</el-form>

			<!-- Step 2: Validation status -->
			<div v-show="currentStep === STEPS.VALIDATE" class="mb-15">
				<el-row v-if="loading" type="flex" justify="center" class="p-20">
					<i class="el-icon-loading" style="font-size: 24px" />
				</el-row>
				<el-alert v-else-if="error" :title="error" type="error" show-icon :closable="false" />
				<el-alert v-else :title="$t('referenceData.validationSuccess')" type="success" show-icon :closable="false" />
			</div>

			<!-- Step 3: Confirm status -->
			<el-alert v-if="currentStep === STEPS.CONFIRM && error" :title="error" type="error" show-icon :closable="false" class="mb-15" />

			<!-- Persistent preview table -->
			<el-divider content-position="left">{{ $t('referenceData.preview') }}</el-divider>
			<el-table
				:data="previewRows"
				border
				size="small"
				max-height="300"
			>
				<el-table-column
					v-for="col in previewColumns"
					:key="col"
					:prop="col"
					:label="col"
					show-overflow-tooltip
					min-width="120"
				/>
			</el-table>
		</div>

		<span slot="footer" class="dialog-footer">
			<el-button @click="handleClose">{{ $t('cancel') }}</el-button>
			<el-button v-if="currentStep > STEPS.UPLOAD" type="default" :disabled="loading" @click="currentStep--">
				{{ $t('referenceData.previous') }}
			</el-button>
			<el-button
				v-if="currentStep < STEPS.CONFIRM"
				type="primary"
				:disabled="!canProceed"
				:loading="loading"
				@click="onNextStep"
			>
				{{ $t('referenceData.next') }}
			</el-button>
			<el-button
				v-if="currentStep === STEPS.CONFIRM"
				type="primary"
				:loading="loading"
				@click="handleConfirm"
			>
				{{ $t('referenceData.confirmImport') }}
			</el-button>
		</span>
	</el-dialog>
</template>

<script>
import axios from 'axios';
import { referenceDataProcessFile } from '@/api/reference_data';
import { parseCsvFile } from '@/utils/csv';

const PREVIEW_ROW_COUNT = 5;

const STEPS = {
	UPLOAD: 0,
	CHOOSE_KEY: 1,
	VALIDATE: 2,
	CONFIRM: 3
};

export default {
	name: 'ReferenceDataImport',
	props: {
		visible: { type: Boolean, default: false },
		participant: { type: String, required: true },
		legalEntity: { type: String, default: null },
		referenceDataType: { type: String, default: null }
	},
	data() {
		return {
			currentStep: STEPS.UPLOAD,
			loading: false,
			error: null,
			key: null,
			selectedKeyColumn: null,
			s3Attachment: null,
			encoding: null,
			delimiter: null,
			previewRows: [],
			previewColumns: []
		};
	},
	computed: {
		dialogVisible: {
			get() {
				return this.visible;
			},
			set(val) {
				this.$emit('update:visible', val);
			}
		},
		canProceed() {
			if (this.loading || this.error) {
				return false;
			}

			if (this.currentStep === STEPS.UPLOAD) {
				return this.previewColumns.length > 0;
			}

			if (this.currentStep === STEPS.CHOOSE_KEY) {
				return this.selectedKeyColumn != null && this.s3Attachment != null;
			}

			return true;
		}
	},
	watch: {
		currentStep() {
			this.error = null;
		}
	},
	created() {
		this.STEPS = STEPS;
	},
	methods: {
		async handleFileChange(file) {
			this.reset();

			if (!/\.csv$/i.test(file.name)) {
				this.error = this.$t('referenceData.invalidFileType');
				return;
			}

			try {
				const { header, results, encoding, delimiter } = await parseCsvFile(file.raw);
				if (!header.length || !results.length) {
					this.error = this.$t('referenceData.emptyFile');
					return;
				}
				this.encoding = encoding;
				this.delimiter = delimiter;
				this.selectedKeyColumn = header[0];
				this.previewColumns = header;
				this.previewRows = results.slice(0, PREVIEW_ROW_COUNT);
			} catch (e) {
				this.error = this.$t('referenceData.parseFailed');
				return;
			}

			// Valid CSV — advance to preview and upload to S3 in the background
			this.currentStep = STEPS.CHOOSE_KEY;
			this.loading = true;
			this.uploadToS3(file.raw, file.name)
				.catch(e => {
					this.error = this.$t('somethingWentWrong');
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async uploadToS3(file, fileName) {
			const payload = {
				participant: this.participant,
				legal_entity: this.legalEntity,
				extension: 'csv'
			};
			const result = await axios.post(
				process.env.VUE_APP_GET_SIGNED_URL_UPLOAD,
				payload,
				{ headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` } }
			);
			const { signed_url, bucket_name, key } = result.data;
			const { url, fields } = signed_url;
			const formData = new FormData();
			Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
			formData.append('file', file);
			await axios.post(url, formData);
			this.s3Attachment = { bucket: bucket_name, key, scheme: 's3', name: fileName };
		},
		onNextStep() {
			if (this.currentStep === STEPS.CHOOSE_KEY) {
				this.key = this.selectedKeyColumn;
				this.currentStep++;
				this.runValidation();
				return;
			}
			this.currentStep++;
		},
		async runValidation() {
			if (!this.referenceDataType) return;
			this.loading = true;
			this.error = null;
			try {
				await referenceDataProcessFile({
					participant: this.participant,
					reference_data_type: this.referenceDataType,
					key: this.key,
					data: this.s3Attachment,
					// See core-platform
					// Optional field to determine scope of reference_data when it is not provided in the file
					//     - If company_id field in file and legal_entity is not None => Exception
					//     - If company_id field in file and legal_entity is None => scope is legal_entity based on company_id
					//     - If company_id field not in file and legal_entity is not None => scope is legal_entity based on legal_entity
					//     - If company_id field not in file and legal_entity is None => scope is participant
					legal_entity: this.previewColumns.includes('company_id') ? null : this.legalEntity,
					persist_data: false,
					encoding: this.encoding,
					delimiter: this.delimiter
				});
			} catch (e) {
				this.error = this.$t('somethingWentWrong');
			} finally {
				this.loading = false;
			}
		},
		async handleConfirm() {
			if (!this.referenceDataType) return;
			this.loading = true;
			this.error = null;
			try {
				await referenceDataProcessFile({
					participant: this.participant,
					reference_data_type: this.referenceDataType,
					key: this.key,
					data: this.s3Attachment,
					// See core-platform
					// Optional field to determine scope of reference_data when it is not provided in the file
					//     - If company_id field in file and legal_entity is not None => Exception
					//     - If company_id field in file and legal_entity is None => scope is legal_entity based on company_id
					//     - If company_id field not in file and legal_entity is not None => scope is legal_entity based on legal_entity
					//     - If company_id field not in file and legal_entity is None => scope is participant
					legal_entity: this.previewColumns.includes('company_id') ? null : this.legalEntity,
					persist_data: true,
					encoding: this.encoding,
					delimiter: this.delimiter
				});

				this.$message.success(this.$t('referenceData.importSuccess'));
				this.$emit('imported');
				this.dialogVisible = false;
			} catch (e) {
				this.error = this.$t('somethingWentWrong');
			} finally {
				this.loading = false;
			}
		},
		reset() {
			this.currentStep = STEPS.UPLOAD;
			this.loading = false;
			this.error = null;
			this.key = null;
			this.selectedKeyColumn = null;
			this.s3Attachment = null;
			this.encoding = null;
			this.delimiter = null;
			this.previewRows = [];
			this.previewColumns = [];
		},
		handleClose() {
			this.reset();
			this.dialogVisible = false;
		}
	}
};
</script>

<style scoped>
.import-step {
	min-height: 120px;
}
.import-upload >>> .el-upload-dragger {
	width: 100%;
}
.import-upload >>> .el-upload {
	width: 100%;
}
</style>
